import { CancellationFee } from "app/shared/model/CancellationFee";
import { getLocalApi } from "./localApi";
import { getSelectedDivisionId } from "app/shared/division/selectedDivisionStorage";
import apiConfig from "app/api/apiConfig.json";
import { ApiErrors } from "./ApiErrors";

export interface SaveCancellationFeeResult {
  ok: boolean;
  message?: string;
  errors?: string[];
  data?: CancellationFee;
  successful?: boolean;
}

function normalizeCentreId(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const match = /^CN(\d+)$/i.exec(trimmed);
  const numericValue = match ? Number(match[1]) : Number(trimmed);
  return Number.isInteger(numericValue) ? numericValue : null;
}

function normalizeSchoolId(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const match = /^School-(\d+)$/i.exec(trimmed);
  const numericValue = match ? Number(match[1]) : Number(trimmed);
  return Number.isInteger(numericValue) ? numericValue : null;
}

function normalizeIds<T>(values: (string | number | undefined | null)[] | undefined, normalizer: (value: string | number | null | undefined) => number | null): number[] {
  if (!Array.isArray(values)) return [];
  return values
    .map(normalizer)
    .filter((id): id is number => id !== null);
}

function toApiPayload(fee: Partial<CancellationFee>) {
  const lastModifiedBy = localStorage.getItem("displayName") || localStorage.getItem("userName") || "System";
  const lastModifiedById = Number(localStorage.getItem("userId")) || 0;
  return {
    productId: fee.id ?? 0,
    productName: (fee.name ?? "").trim(),
    lastModifiedDate: fee.lastModifiedDate ?? new Date().toISOString(),
    lastModifiedBy,
    lastModifiedById,
    entityUpdate: fee.entityUpdate ?? false,
    accountCategoryId: fee.accountCategoryId ?? 0,
    accountCategoryText: fee.accountCategoryText ?? "",
    productCategoryId: fee.productCategoryId ?? 0,
    productCategoryText: fee.productCategoryText ?? "",
    active: fee.active ?? fee.isPublished ?? true,
    schools: fee.schools ?? [],
    schoolIds: normalizeIds(fee.schoolIds, normalizeSchoolId),
    centreIds: normalizeIds(fee.centreIds, normalizeCentreId),
  };
}

export async function saveCancellationFee(fee: Partial<CancellationFee>, divisionId?: number): Promise<SaveCancellationFeeResult> {
  const localApi = await getLocalApi();
  const divId = divisionId ?? getSelectedDivisionId();
  const endpoint = apiConfig.cancellationfees_save.replace("{divisionId}", String(divId));
  const payload = toApiPayload(fee);
  const res = await localApi.call(endpoint, "PUT", JSON.stringify(payload));
  return handleSaveResult(res);
}

export async function createCancellationFee(fee: Partial<CancellationFee>): Promise<SaveCancellationFeeResult> {
  return saveCancellationFee(fee);
}

export async function updateCancellationFee(fee: CancellationFee): Promise<SaveCancellationFeeResult> {
  return saveCancellationFee(fee);
}

async function handleSaveResult(response: Response): Promise<SaveCancellationFeeResult> {
  const fallbackError = "Failed to save cancellation fee";

  let rawText = "";
  try {
    rawText = await response.text();
  } catch {
    rawText = "";
  }

  const safeParse = () => {
    if (!rawText) return undefined;
    try {
      return JSON.parse(rawText);
    } catch {
      return undefined;
    }
  };

  const parsed = safeParse();
  const errorMessages = ApiErrors.extractAllErrors(parsed, response, fallbackError);

  if (response.ok) {
    if (errorMessages.length > 0) {
      return {
        ok: false,
        successful: parsed?.successful,
        errors: errorMessages,
        message: ApiErrors.formatErrors(errorMessages),
      };
    }
    if (parsed?.successful === false) {
      return {
        ok: false,
        successful: parsed.successful,
        errors: errorMessages.length > 0 ? errorMessages : undefined,
        message: ApiErrors.formatErrors(errorMessages) || fallbackError,
      };
    }
    if (!rawText) return { ok: true };
    const raw = parsed?.data ?? parsed;
    const data: CancellationFee | undefined = raw
      ? {
          id: raw.productId ?? raw.id ?? 0,
          name: raw.productName ?? raw.name ?? "",
          divisionId: raw.divisionId ?? 0,
          accountCategoryId: raw.accountCategoryId,
          productCategoryId: raw.productCategoryId,
          isPublished: raw.active ?? false,
          active: raw.active ?? false,
          lastModifiedDate: raw.lastModifiedDate ?? null,
          lastUpdatedBy: raw.lastModifiedBy ?? "",
          entityUpdate: raw.entityUpdate,
        }
      : undefined;
    return { ok: true, successful: parsed?.successful, data };
  }

  const message = ApiErrors.formatErrors(errorMessages);
  if (response.status === 400 && parsed) {
    return {
      ok: false,
      successful: parsed.successful,
      errors: errorMessages.length > 0 ? errorMessages : undefined,
      message,
    };
  }
  return { ok: false, message, errors: errorMessages.length > 0 ? errorMessages : undefined };
}